import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Loading from '../Common/Loading';
import CustomAlert from '../Common/CustomAlert';
import { TextWrap } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;
const ROWS_PER_PAGE = 20;

interface EmailLog {
    id: number;
    created_on: string;
    from: string;
    to: string;
    subject: string;
    body: string;
    sent_on: string;
    error_message: string | null;
}

interface WeekendOption {
    label: string;
    value: string;
}

const styles = {
    body: {
        margin: '0px',
        fontFamily: 'Trebuchet MS, Arial, Helvetica, sans-serif',
        fontSize: '10pt',
        backgroundColor: 'white',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column' as const,
    },
    container: {
        padding: '5px',
        flex: 1,
        display: 'flex',
        flexDirection: 'column' as const,
        overflow: 'hidden',
    },
    titleBar: {
        color: '#2859BF',
        backgroundColor: '#c7ddfd',
        padding: '5px',
        fontSize: '12pt',
        fontFamily: 'Arial',
        width: '99.5%',
        textAlign: 'center' as const,
        fontWeight: 'bold',
        border: '1px solid #80A0FF',
        borderBottom: '0px',
    },
    headerRow: {
        fontFamily: 'Arial',
        fontSize: '10pt',
        backgroundColor: '#c7ddfd',
        color: '#2859ac',
        height: '32px',
        textAlign: 'left' as const,
    },
    headerCell: {
        padding: '8px',
        borderRight: '1px solid #80A0FF',
        borderBottom: '1px solid #80A0FF',
        textAlign: 'left' as const,
        whiteSpace: 'nowrap' as const,
        overflow: 'hidden',
        fontWeight: 'normal' as const,
        textWrap: "nowrap"
    },

    row: {
        fontFamily: 'Arial',
        fontSize: '11pt',
        backgroundColor: 'white',
        verticalAlign: 'top',
        cursor: 'pointer',
        minHeight: '20px',
        color: 'black',
    },
    altRow: {
        fontFamily: 'Trebuchet MS',
        fontSize: '11pt',
        backgroundColor: '#F4FAFF',
        verticalAlign: 'top',
        cursor: 'pointer',
        minHeight: '20px',
        color: 'black',
    },
    selectedRow: {
        fontFamily: 'Trebuchet MS',
        fontSize: '11pt',
        backgroundColor: 'orange',
        color: 'White',
        verticalAlign: 'top',
        cursor: 'pointer',
        minHeight: '20px',
    },
    cell: {
        padding: '8px',
        fontSize: '10pt',
        borderRight: '1px solid #80A0FF',
        fontFamily: 'Arial',
        whiteSpace: 'nowrap' as const,
        overflow: 'hidden' as const,
        textOverflow: 'ellipsis' as const,
    },
    paginationBar: {
        backgroundColor: '#c7ddfd',
        color: '#2859ac',
        fontFamily: 'Arial',
        fontSize: '10pt',
        fontWeight: 'bold' as const,
        textAlign: 'center' as const,
        padding: '8px',
        border: 'solid 1px #80A0FF',
        borderTop: 'solid 1px #80A0FF',
        width: '99%',
    },
    navLink: {
        textDecoration: 'none',
        color: '#003366',
        margin: '0 5px',
        cursor: 'pointer',
    },
    button: {
        backgroundColor: '#E5E5E5',
        fontFamily: 'Arial',
        fontSize: '12pt',
        color: 'black',
        border: '1px solid #4b4b4b',
        height: '25px',
        minWidth: '100px',
        cursor: 'pointer',
        whiteSpace: 'nowrap' as const,
        padding: '0 10px',
        borderRadius: '3px',
    },
    buttonDisabled: {
        backgroundColor: '#e0e0e0',
        fontFamily: 'Arial',
        fontSize: '11pt',
        color: '#a0a0a0',
        border: '1px solid #4b4b4b',
        height: '25px',
        minWidth: '100px',
        cursor: 'default',
        whiteSpace: 'nowrap' as const,
        backgroundImage: 'none',
        borderRadius:"2px"
    },
    scrollableDiv: {
        height: 'calc(100vh - 140px)',
        overflowY: 'auto' as const,
        overflowX: 'auto' as const,
        border: '1px solid #80A0FF',
        width: '100%',
        backgroundColor: 'white',
    },
    dropdown: {
        fontFamily: 'Arial',
        fontSize: '11pt',
        border: '1px solid #4e4e4e',
        height: '25px',
        padding: '2px',
        marginLeft: '5px',
        borderRadius:"2px"
    },

};

function EL_MailsSent() {
    const [searchParams] = useSearchParams();
    const [emails, setEmails] = useState<EmailLog[]>([]);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);

    const [weekendDates, setWeekendDates] = useState<WeekendOption[]>([]);
    const [selectedDate, setSelectedDate] = useState<string>("");
    const [alertInfo, setAlertInfo] = useState({ show: false, message: '' });

    const [statusMsg, setStatusMsg] = useState<{ text: string, color: string } | null>(null);

    const getParam = (key: string) => {
        return searchParams.get(key) || new URLSearchParams(window.location.search).get(key) || '';
    };
    const schoolCode = getParam('C') || getParam('SchoolCode');
    const familyCode = getParam('F') || getParam('FamilyCode');

    const loadWeekendDates = () => {
        const today = new Date();
        const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

        // Sunday of the most recently completed weekend.
        // If today IS Sunday, dayOfWeek is 0, so this resolves to today.
        const daysSinceSunday = dayOfWeek;
        let currentWeekend = new Date(today);
        currentWeekend.setDate(today.getDate() - daysSinceSunday);

        const options: WeekendOption[] = [];
        let tempDate = new Date(currentWeekend);
        for (let i = 0; i < 11; i++) {
            const dd = String(tempDate.getDate()).padStart(2, '0');
            const mm = String(tempDate.getMonth() + 1).padStart(2, '0');
            const yyyy = tempDate.getFullYear();
            options.push({ label: `${dd}/${mm}/${yyyy}`, value: `${dd}/${mm}/${yyyy}` });
            tempDate.setDate(tempDate.getDate() - 7);
        }
        setWeekendDates(options);
        if (options.length > 0) setSelectedDate(options[0].value);
    };

    const fetchEmails = async () => {
        setLoading(true);
        setStatusMsg(null);
        try {
            const response = await fetch(`${API_BASE_URL}api/get_sent_emails?SchoolCode=${schoolCode}&FamilyCode=${familyCode}`);
            const result = await response.json();
            if (result.status === 'success') {
                setEmails(result.data);
                setCurrentPage(1);
            } else {
                setStatusMsg({ text: result.message || "Failed to load emails", color: 'red' });
            }
        } catch (error: any) {
            setStatusMsg({ text: error.message || "Network Error", color: 'red' });
        } finally {
            setLoading(false);
        }
    };

    const handleSendAgain = async () => {
        if (!selectedId) {
            setAlertInfo({ show: true, message: 'Please select a row' });
            return;
        }
        setStatusMsg({ text: "Sending...", color: "blue" });

        try {
            const response = await fetch(`${API_BASE_URL}api/resend_email`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email_id: selectedId })
            });
            const result = await response.json();
            if (result.status === 'success') {
                setStatusMsg({ text: "Email queued for resending.", color: "green" });
                fetchEmails();
            } else {
                setStatusMsg({ text: "Error: " + result.message, color: "red" });
            }
        } catch (error) {
            setStatusMsg({ text: "Failed to resend email.", color: "red" });
        }
    };

    const handleGenerateAndSend = async () => {
        if (!selectedDate) return;
        if (!window.confirm(`Generate and send email for weekend ${selectedDate}?`)) return;

        setStatusMsg({ text: "Generating...", color: "blue" });
        try {
            const response = await fetch(`${API_BASE_URL}api/regenerate_weekly_email`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ date: selectedDate, school_code: schoolCode, family_code: familyCode })
            });
            const result = await response.json();
            if (result.status === 'success') {
                setStatusMsg({ text: result.message || "Email generated and sent.", color: "green" });
                fetchEmails();
            } else {
                setStatusMsg({ text: "Error: " + result.message, color: "red" });
            }
        } catch (error) {
            setStatusMsg({ text: "Failed to generate email.", color: "red" });
        }
    };

    useEffect(() => {
        loadWeekendDates();
        if (schoolCode && familyCode) fetchEmails();
        else {
            setLoading(false);
            setStatusMsg({ text: "Missing SchoolCode (C) or FamilyCode (F)", color: 'red' });
        }
    }, [schoolCode, familyCode]);

    const totalPages = Math.ceil(emails.length / ROWS_PER_PAGE);
    const startIndex = (currentPage - 1) * ROWS_PER_PAGE;
    const currentData = emails.slice(startIndex, startIndex + ROWS_PER_PAGE);

    const handlePageChange = (direction: string) => {
        if (direction === 'first') setCurrentPage(1);
        if (direction === 'prev' && currentPage > 1) setCurrentPage(p => p - 1);
        if (direction === 'next' && currentPage < totalPages) setCurrentPage(p => p + 1);
        if (direction === 'last') setCurrentPage(totalPages);
    };

    const formatDate = (dateString: string) => dateString;


    return (
        <div style={styles.body}>
            <div style={styles.container}>
                <div style={styles.titleBar}>
                    eMails Sent to Family: {schoolCode} / {familyCode}
                </div>

                {statusMsg && (
                    <div style={{ textAlign: 'center', color: statusMsg.color, fontWeight: 'bold', marginBottom: '5px' }}>
                        {statusMsg.text}
                    </div>
                )}

                <div style={styles.scrollableDiv}>
                    <table cellPadding={0} cellSpacing={0} style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                        <colgroup>
                            <col style={{ width: '45px' }} />
                            <col style={{ width: '60px' }} />
                            <col style={{ width: '130px' }} />
                            <col style={{ width: '150px' }} />
                            <col style={{ width: '150px' }} />
                            <col style={{ width: '200px' }} />
                            <col style={{ width: '800px' }} />
                            <col style={{ width: '130px' }} />
                            <col style={{ width: '250px' }} />
                        </colgroup>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                            <tr style={styles.headerRow}>
                                <th style={styles.headerCell}>#</th>
                                <th style={styles.headerCell}>ID</th>
                                <th style={styles.headerCell}>Created On</th>
                                <th style={styles.headerCell}>From</th>
                                <th style={styles.headerCell}>To</th>
                                <th style={styles.headerCell}>Subject</th>
                                <th style={{ ...styles.headerCell, width: '800px' }}>Body</th>
                                <th style={styles.headerCell}>SentOn</th>
                                <th style={styles.headerCell}>ErrorMessage</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={9} style={{ padding: '10px', textAlign: 'center' }}><Loading /></td></tr>
                            ) : (
                                currentData.map((row, index) => {
                                    const isSelected = selectedId === row.id;
                                    let rowStyle = index % 2 === 0 ? styles.altRow : styles.row;
                                    if (isSelected) rowStyle = styles.selectedRow;

                                    return (
                                        <tr
                                            key={row.id}
                                            style={rowStyle}
                                            onClick={() => setSelectedId(row.id)}
                                        >
                                            <td style={styles.cell}>
                                                {startIndex + index + 1}
                                            </td>

                                            <td style={styles.cell}>
                                                {row.id}
                                            </td>
                                            <td style={styles.cell}>{formatDate(row.created_on)}</td>
                                            <td style={styles.cell}>{row.from}</td>
                                            <td style={styles.cell}>{row.to}</td>
                                            <td style={styles.cell}>{row.subject}</td>
                                            <td
                                                style={{
                                                    ...styles.cell,
                                                    width: '800px',
                                                    maxWidth: '800px',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'normal',
                                                }}
                                            >
                                                <div
                                                    className="emailBody"
                                                    dangerouslySetInnerHTML={{
                                                        __html: row.body

                                                            // Make every font-weight declaration normal
                                                            .replace(/font-weight\s*:\s*[^;'" ]+/gi, "font-weight:normal")

                                                            // If a tag already has a style attribute, append font-weight:normal
                                                            .replace(
                                                                /<p([^>]*?)style=(['"])(.*?)\2([^>]*)>/gi,
                                                                '<p$1style=$2$3;font-weight:normal;$2$4>'
                                                            )

                                                            // If a p tag has no style attribute
                                                            .replace(/<p(?![^>]*style=)([^>]*)>/gi, '<p$1 style="font-weight:normal;">')

                                                            // Keep this message bold
                                                            .replace(
                                                                /<b>(.*?)<\/b>/gi,
                                                                '<b style="font-weight:bold;">$1</b>'
                                                            )

                                                            // Keep strong tags bold
                                                            .replace(
                                                                /<strong>(.*?)<\/strong>/gi,
                                                                '<strong style="font-weight:bold;">$1</strong>'
                                                            )
                                                    }}
                                                />
                                            </td>
                                            <td style={styles.cell}>{formatDate(row.sent_on)}</td>
                                            <td style={styles.cell}>{row.error_message || '-'}</td>
                                        </tr>
                                    );
                                })
                            )}
                            {currentData.length === 0 && !loading && (
                                <tr>
                                    <td colSpan={9} style={{ padding: '10px', textAlign: 'center', color: '#555' }}>
                                        No Sent Mails Found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div style={styles.paginationBar}>
                    <span style={styles.navLink} onClick={() => handlePageChange('first')}>&lt;&lt;</span>
                    <span style={styles.navLink} onClick={() => handlePageChange('prev')}>&lt;</span>
                    <span style={{ margin: '0 10px' }}>
                        {currentPage} of {totalPages || 1}
                    </span>
                    <span style={styles.navLink} onClick={() => handlePageChange('next')}>&gt;</span>
                    <span style={styles.navLink} onClick={() => handlePageChange('last')}>&gt;&gt;</span>
                </div>

                <div style={{ paddingTop: '10px' }}>
                    <table style={{ width: '100%' }}>
                        <tbody>
                            <tr>
                                <td style={{textAlign:"left", width:"20%"}}>
                                    <button
                                        style={{...styles.button, marginRight:"10px"}}
                                        onClick={() => window.close()}
                                    >
                                        Close
                                    </button>

                                         <button
                                        style={styles.button}
                                        onClick={handleSendAgain}
                                    >
                                        Send Again
                                    </button>

                                </td>

                                <td style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            

                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <span style={{ fontSize: '12pt', fontFamily: 'Arial', fontWeight:"bold"}}>For Weekend Date:</span>
                                        <select
                                            style={styles.dropdown}
                                            value={selectedDate}
                                            onChange={(e) => setSelectedDate(e.target.value)}
                                        >
                                            {weekendDates.map(d => (
                                                <option key={d.value} value={d.value}>{d.label}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <button
                                        // style={{ ...styles.button, minWidth: '220px' }}
                                        style={{
                                            ...(selectedId ? styles.buttonDisabled : styles.buttonDisabled),
                                            minWidth: '220px',
                                        }}
                                        onClick={handleGenerateAndSend}
                                        disabled={true}
                                    >
                                        Generate AND Send mail Again
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            {alertInfo.show && (
                <CustomAlert
                    message={alertInfo.message}
                    onClose={() => setAlertInfo({ ...alertInfo, show: false })}
                />
            )}
        </div>
    );
}

export default EL_MailsSent;